import type { TableColumnStaticProps } from '@/types/tableTypes';
import {
    groupColumnsForInspector,
    isMissingFromSearchDocument,
    type InspectorFieldGroup,
} from '@/composables/searchInspectorFieldGroups';

/**
 * A record page's layout: which fields make the top line and the title, which
 * fields show as chips under the title, which
 * fields show in Details and in what sections, and how they are grouped and
 * sorted. The same shape is stored by the backend in `record_layouts.definition`
 * (spec: docs/specs/macro-style-layout.md, "Data").
 *
 * Field names are models.json column ids, for example `player_time_zone`. They
 * are not checked when saved, because models.json changes between releases. A
 * name that no longer exists is skipped when the layout is drawn, and kept in
 * the saved layout, so the field comes back if it returns.
 */
export interface RecordLayoutSection {
    key: string;
    label: string;
    fields: string[];
}

export interface RecordLayoutDefinition {
    version: 1;
    /**
     * The small line above the record's name, as fields joined by " · ", for
     * example "Player 1 · Cafe Nero · Brannon Legros". `_record` is "Record type
     * and ID". Absent means the default, {@see defaultTopLine}. Empty means no
     * top line.
     */
    top_line?: string[];
    /**
     * The record's name, as fields joined by " · ". Never empty when present.
     * Absent means the display name, {@see DISPLAY_NAME_FIELD}.
     */
    title?: string[];
    /** The chips under the name. Named "Chips" in Customize, kept as `header` so saved layouts keep working. */
    header: string[];
    sections: RecordLayoutSection[];
    group_by: 'section' | 'none';
    sort: 'custom' | 'alpha';
    /**
     * Whether the pinned chips show their field's label, as "Volume 89", or
     * only the value, as "89", with the label in a tooltip. One setting for the
     * whole layout. Absent means "show", so layouts saved before it existed
     * keep their labels.
     */
    chip_labels?: 'show' | 'hide';
    /**
     * Names the person gave fields, by field: for example
     * `{ "location_zone_player_stream_mode": "Stream" }` turns the chip
     * "Location Zone Player Stream Mode Offline" into "Stream Offline". Used for
     * the chips and for Details. Absent, or a field missing from it, means the
     * field's own name.
     */
    field_labels?: Record<string, string>;
}

/** The longest name a field can be given. The backend refuses anything longer. */
export const MAX_FIELD_LABEL_LENGTH = 60;

/** The names given to fields in a layout, empty when none were. */
export function fieldLabelsOf(definition: RecordLayoutDefinition): Record<string, string> {
    return definition.field_labels ?? {};
}

/**
 * A copy of a layout with one field renamed. An empty name, or the field's own
 * name, removes the rename, so the field follows its own name again.
 */
export function withFieldLabel(
    definition: RecordLayoutDefinition,
    field: string,
    label: string,
    ownName: string,
): RecordLayoutDefinition {
    const next = { ...fieldLabelsOf(definition) };
    const trimmed = label.trim().slice(0, MAX_FIELD_LABEL_LENGTH);
    if (trimmed === '' || trimmed === ownName) {
        delete next[field];
    } else {
        next[field] = trimmed;
    }
    return { ...definition, field_labels: next };
}

/** "Record type and ID", such as "Player 1", as an item in the top line or the title. */
export const RECORD_FIELD = '_record';

/**
 * The record's display name, such as "Ada Wilkinson", as an item in the top
 * line or the title. Most record types have no column by this name, so it is
 * an item of its own, like {@see RECORD_FIELD}.
 */
export const DISPLAY_NAME_FIELD = 'display_string';

/** The top line before anyone changes it. Fields a record type lacks are left out. */
export const BUILT_IN_TOP_LINE_FIELDS: readonly string[] = [RECORD_FIELD, 'location', 'account'];

/** The items the top line and the title offer that are not columns, with the names Customize shows. */
export const HEADING_ITEMS: readonly { id: string; label: string }[] = [
    { id: RECORD_FIELD, label: 'Record type and ID' },
    { id: DISPLAY_NAME_FIELD, label: 'Display name' },
];

/** The default top line for a record type: "Record type and ID", location, account, keeping the ones it has. */
export function defaultTopLine(columns: TableColumnStaticProps[]): string[] {
    const columnIds = new Set(columns.map((column) => column.id));
    return BUILT_IN_TOP_LINE_FIELDS.filter((field) => field === RECORD_FIELD || columnIds.has(field));
}

/** The fields of a layout's top line, counting an absent one as the default. */
export function topLineOf(definition: RecordLayoutDefinition, columns: TableColumnStaticProps[]): string[] {
    return definition.top_line ?? defaultTopLine(columns);
}

/** The fields of a layout's title, counting an absent or empty one as the display name. */
export function titleOf(definition: RecordLayoutDefinition): string[] {
    return definition.title && definition.title.length > 0 ? definition.title : [DISPLAY_NAME_FIELD];
}

/** A copy of a layout with a new top line. An empty one removes the row. */
export function withTopLine(definition: RecordLayoutDefinition, fields: string[]): RecordLayoutDefinition {
    return { ...definition, top_line: fields };
}

/**
 * A copy of a layout with a new title. An empty title leaves `title` out,
 * because the backend refuses an empty list. With no title the heading
 * falls back to the display name, so a record always has a name.
 */
export function withTitle(definition: RecordLayoutDefinition, fields: string[]): RecordLayoutDefinition {
    if (fields.length > 0) return { ...definition, title: fields };
    const withoutTitle = { ...definition };
    delete withoutTitle.title;
    return withoutTitle;
}

/** The chip label setting a layout has, counting an absent one as "show". */
export function chipLabelsOf(definition: RecordLayoutDefinition): 'show' | 'hide' {
    return definition.chip_labels === 'hide' ? 'hide' : 'show';
}

/** A built-in layout as written by hand: section keys are made from their names. */
export interface HandWrittenLayout {
    top_line: string[];
    title: string[];
    /** The chips. */
    header: string[];
    sections: { label: string; fields: string[] }[];
}

/**
 * The layout each record type shows before anyone saves one: the top line, the
 * title, the chips and the Details sections Joel approved on 2026-10-19. Every
 * field in no section starts under "Not shown" in Customize. The list per
 * record type, with the reasons, is in
 * docs/specs/record-properties-panel/default-layouts.md. A unit test checks
 * every name here is a real column.
 */
export const BUILT_IN_LAYOUTS: Readonly<Record<string, HandWrittenLayout>> = {
    Account: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['account_status', 'account_manager_user', 'is_tpg_account', 'is_onboarding'],
        sections: [
            { label: 'Account', fields: ['playlist_manager_group', 'onboarding_user', 'website_url', 'is_archived'] },
            { label: 'Locations and zones', fields: ['locations', 'location_zones', 'contacts'] },
            { label: 'Playback rules', fields: ['can_skip', 'can_vote'] },
        ],
    },
    Location: {
        top_line: [RECORD_FIELD, 'account'],
        title: [DISPLAY_NAME_FIELD],
        header: ['account', 'location_status', 'category', 'region', 'store_number'],
        sections: [
            { label: 'Location', fields: ['franchisee', 'startup_date', 'already_open', 'is_onboarding', 'onboarding_user', 'is_archived'] },
            { label: 'Contacts', fields: ['contacts'] },
        ],
    },
    LocationZone: {
        top_line: [RECORD_FIELD, 'location', 'account'],
        title: [DISPLAY_NAME_FIELD],
        header: ['location', 'location_zone_status', 'time_zone', 'location_zone_tier', 'is_tpg_location_zone'],
        sections: [
            {
                label: 'Music',
                fields: ['master_location_zone', 'playlist_manager_group', 'content_style', 'buckets', 'location_zone_custom_playlists'],
            },
            { label: 'Players', fields: ['location_zone_players', 'subscribers_count'] },
            { label: 'Follow-up', fields: ['onboarding_manager_user', 'is_onboarding', 'last_followup_date', 'is_past_due', 'is_archived'] },
        ],
    },
    Contact: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['email', 'brand', 'title', 'instore_location_zone'],
        sections: [
            { label: 'Contact', fields: ['owners', 'votes_count', 'is_tpg_contact', 'cw_link_sent', 'cw_link_sent_date', 'is_archived'] },
        ],
    },
    LocationZonePlayer: {
        top_line: [RECORD_FIELD, 'location', 'account'],
        title: [DISPLAY_NAME_FIELD],
        // Online and Last checkin are chips because Details never draws them on a
        // player (inspectorExcludeFieldsFor). Account and location are in the top line.
        header: [
            'player_data_is_online',
            'player_data_updated_at',
            'location_zone_player_stream_mode',
            'volume',
            'player_time_zone',
            'brand_id',
        ],
        sections: [
            { label: 'Now playing', fields: ['player_data_current_track_code', 'player_data_current_playlist_name', 'player_data_playing'] },
            { label: 'Content', fields: ['player_data_last_loaded_date', 'player_data_referenced_content_missing'] },
            {
                label: 'Device',
                fields: [
                    'player_type',
                    'player_data_model_type',
                    'player_data_version',
                    'player_data_remote_ip',
                    'player_data_disk_usage_percentage',
                ],
            },
            { label: 'Organization', fields: ['tags'] },
        ],
    },
    Bucket: {
        top_line: [RECORD_FIELD, 'location', 'account'],
        title: [DISPLAY_NAME_FIELD],
        header: ['location_zone', 'bucket_type_id', 'is_base_bucket', 'max_percentage_of_manual_tracks'],
        sections: [
            {
                label: 'Bucket',
                fields: ['short_description', 'base_bucket', 'min_percentage_of_preferred_tracks', 'ignores_base_bucket_genres', 'is_archived'],
            },
            {
                label: 'Rules',
                fields: ['bucket_genres', 'bucket_tag_excludes'],
            },
        ],
    },
    LocationZoneCustomPlaylist: {
        top_line: [RECORD_FIELD, 'location', 'account'],
        title: [DISPLAY_NAME_FIELD],
        header: ['location_zone', 'location_zone_custom_playlist_status', 'shuffle', 'expiration_date'],
        sections: [
            {
                label: 'Playlist',
                fields: [
                    'details',
                    'source_bucket',
                    'is_published',
                    'is_visible_to_client',
                    'should_download_to_players',
                    'special_event',
                    'is_sample_list',
                    'status_expiration',
                ],
            },
        ],
    },
    ContactLoginCustomPlaylist: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['contact_login', 'created_by_user'],
        sections: [{ label: 'Playlist', fields: ['contact_login_custom_playlist_tracks', 'is_archived', 'created_at', 'updated_at'] }],
    },
    LiveTrack: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['artist', 'album', 'genre', 'duration', 'rating', 'track_match_status'],
        sections: [
            {
                label: 'Track',
                fields: ['active', 'disabled_reason', 'record_label', 'isrc', 'code', 'parental_advisory_type', 'remix_name', 'uploaded_date'],
            },
            { label: 'Usage', fields: ['playlist_count', 'latest_live_track_action', 'lyrics_check_status'] },
        ],
    },
    Artist: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['active', 'stream_only'],
        sections: [{ label: 'Artist', fields: ['aliases', 'tags'] }],
    },
    Album: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['artist', 'record_label', 'genre', 'release_date'],
        sections: [{ label: 'Album', fields: ['upc', 'code', 'active'] }],
    },
    RecordLabel: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['parent_record_label', 'is_active', 'dpid'],
        sections: [{ label: 'Record label', fields: ['isrc_is_required', 'categories'] }],
    },
    ContactLoginStation: {
        top_line: [RECORD_FIELD],
        title: [DISPLAY_NAME_FIELD],
        header: ['contact_login', 'contact_login_station_source_type', 'time_zone', 'should_expire'],
        sections: [
            {
                label: 'Station',
                fields: ['contact', 'vote_destination_location_zone', 'expiration', 'is_broken'],
            },
            { label: 'Publishing', fields: ['web_published', 'mobile_published', 'clientweb_published', 'web_login_disabled'] },
        ],
    },
    RawDdexTrack: {
        top_line: [RECORD_FIELD],
        // A DDEX track has no display name, so the title is the track's own Title field.
        title: ['title'],
        header: ['artists', 'album_name', 'isrc', 'raw_ddex_track_takedown_status', 'message_date'],
        sections: [
            { label: 'Release', fields: ['sub_title', 'record_label_name', 'genre_name', 'duration', 'parental_advisory', 'catalog_number'] },
            { label: 'Source', fields: ['raw_ddex_party_sender', 'raw_ddex_ern_message'] },
        ],
    },
    LiveTrackAction: {
        top_line: [RECORD_FIELD],
        // The display name reads "search #2", the source and the ID. The track's name says more.
        title: ['live_track'],
        header: ['live_track_action_status', 'source', 'performed_by_user', 'created_at'],
        sections: [
            {
                label: 'Action',
                fields: [
                    'live_track',
                    'error_messages',
                    'can_retry',
                    'update_fields',
                    'update_audio_file',
                    'update_cover_art',
                    'verify_files_before_update',
                ],
            },
            { label: 'Related', fields: ['rollback_to_action', 'raw_ddex_track'] },
        ],
    },
};

function isStringList(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isLabelMap(value: unknown): value is Record<string, string> {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.values(value).every((label) => typeof label === 'string')
    );
}

/** True when a value read from the server has the version-1 shape. */
export function isRecordLayoutDefinition(value: unknown): value is RecordLayoutDefinition {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return (
        candidate.version === 1 &&
        (candidate.group_by === 'section' || candidate.group_by === 'none') &&
        (candidate.sort === 'custom' || candidate.sort === 'alpha') &&
        (candidate.chip_labels === undefined || candidate.chip_labels === 'show' || candidate.chip_labels === 'hide') &&
        (candidate.field_labels === undefined || isLabelMap(candidate.field_labels)) &&
        (candidate.top_line === undefined || isStringList(candidate.top_line)) &&
        (candidate.title === undefined || (isStringList(candidate.title) && candidate.title.length > 0)) &&
        isStringList(candidate.header) &&
        Array.isArray(candidate.sections) &&
        candidate.sections.every(
            (section) =>
                typeof section === 'object' &&
                section !== null &&
                typeof (section as RecordLayoutSection).key === 'string' &&
                typeof (section as RecordLayoutSection).label === 'string' &&
                isStringList((section as RecordLayoutSection).fields),
        )
    );
}

/** Your layout, else Everyone's default, else the built-in one. */
export function chooseLayout(
    mine: RecordLayoutDefinition | null,
    everyonesDefault: RecordLayoutDefinition | null,
    builtIn: RecordLayoutDefinition,
): RecordLayoutDefinition {
    return mine ?? everyonesDefault ?? builtIn;
}

function sectionKey(title: string): string {
    return title
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * The layout a record type has when nobody has saved one: its hand-written
 * layout from {@see BUILT_IN_LAYOUTS}. A record type with none shows every
 * field, in the sections the side peek's grouping makes, with no chips.
 */
export function builtInLayout(entityId: string, columns: TableColumnStaticProps[]): RecordLayoutDefinition {
    const handWritten = BUILT_IN_LAYOUTS[entityId];
    if (handWritten) {
        return {
            version: 1,
            top_line: [...handWritten.top_line],
            title: [...handWritten.title],
            header: [...handWritten.header],
            sections: handWritten.sections.map((section) => ({ key: sectionKey(section.label), label: section.label, fields: [...section.fields] })),
            group_by: 'section',
            sort: 'custom',
            chip_labels: 'show',
        };
    }
    return {
        version: 1,
        top_line: defaultTopLine(columns),
        title: [DISPLAY_NAME_FIELD],
        header: [],
        sections: groupColumnsForInspector(entityId, columns).map((group) => ({
            key: sectionKey(group.title),
            label: group.title,
            fields: group.columns.map((column) => column.id),
        })),
        group_by: 'section',
        sort: 'custom',
        chip_labels: 'show',
    };
}

export interface ApplyLayoutOptions {
    /** Fields that never render here, for example ones the page draws elsewhere. */
    excludeFields?: string[];
    /**
     * The record type. When given, a related field its search document never
     * holds is skipped, so a layout saved before that rule draws no empty row.
     */
    entityId?: string;
}

export interface AppliedLayout {
    headerColumns: TableColumnStaticProps[];
    groups: InspectorFieldGroup[];
}

/** A layout turned into the columns a record page draws. */
export function applyLayout(
    definition: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
    options: ApplyLayoutOptions = {},
): AppliedLayout {
    const excluded = new Set(options.excludeFields ?? []);
    const labels = fieldLabelsOf(definition);
    // A renamed field draws under its new name everywhere the layout reaches.
    const columnsById = new Map(
        columns.map((column) => [column.id, labels[column.id] ? { ...column, header: labels[column.id] } : column]),
    );

    const resolve = (fields: string[]): TableColumnStaticProps[] =>
        fields
            .map((field) => columnsById.get(field))
            .filter(
                (column): column is TableColumnStaticProps =>
                    column !== undefined &&
                    !excluded.has(column.id) &&
                    column.dataType !== 'display' &&
                    !(options.entityId !== undefined && isMissingFromSearchDocument(options.entityId, column)),
            );

    const sortColumns = (list: TableColumnStaticProps[]) =>
        definition.sort === 'alpha' ? [...list].sort((first, second) => first.header.localeCompare(second.header)) : list;

    let groups: InspectorFieldGroup[] = definition.sections.map((section) => ({
        title: section.label,
        columns: sortColumns(resolve(section.fields)),
    }));

    if (definition.group_by === 'none') {
        groups = [{ title: 'Details', columns: sortColumns(groups.flatMap((group) => group.columns)) }];
    }

    return {
        headerColumns: resolve(definition.header),
        groups: groups.filter((group) => group.columns.length > 0),
    };
}

/** The longest name a section can be given, the same ceiling as a field's. */
export const MAX_SECTION_LABEL_LENGTH = MAX_FIELD_LABEL_LENGTH;

/**
 * A copy of a layout with a new, empty section at the end, and the new
 * section's key. Keys of your own start with "custom-", so a built-in section
 * added in a later release cannot share one.
 */
export function withNewSection(definition: RecordLayoutDefinition, label = 'New section'): { layout: RecordLayoutDefinition; key: string } {
    const taken = new Set(definition.sections.map((section) => section.key));
    let number = 1;
    while (taken.has(`custom-${number}`)) number += 1;
    const key = `custom-${number}`;
    return { layout: { ...definition, sections: [...definition.sections, { key, label, fields: [] }] }, key };
}

/** A copy of a layout with one section renamed. An empty name keeps the old one. */
export function withSectionLabel(definition: RecordLayoutDefinition, key: string, label: string): RecordLayoutDefinition {
    const trimmed = label.trim().slice(0, MAX_SECTION_LABEL_LENGTH);
    if (trimmed === '') return definition;
    return { ...definition, sections: definition.sections.map((section) => (section.key === key ? { ...section, label: trimmed } : section)) };
}

/**
 * A copy of a layout without one section. Its fields move to the end of the
 * section above it, or of the one below when it was first, so deleting a
 * group never hides a field. The last section cannot be deleted.
 */
export function withoutSection(definition: RecordLayoutDefinition, key: string): RecordLayoutDefinition {
    const index = definition.sections.findIndex((section) => section.key === key);
    if (index < 0 || definition.sections.length < 2) return definition;
    const removed = definition.sections[index];
    const heirIndex = index > 0 ? index - 1 : 1;
    const sections = definition.sections.map((section, position) =>
        position === heirIndex
            ? { ...section, fields: [...section.fields, ...removed.fields.filter((field) => !section.fields.includes(field))] }
            : section,
    );
    return { ...definition, sections: sections.filter((section) => section.key !== key) };
}

/**
 * A copy of a layout with one section's fields replaced. A field added here
 * leaves whichever other section held it, so a field shows in one section at
 * most. That is how a field moves into a group of your own: add it there.
 */
export function withSectionFields(definition: RecordLayoutDefinition, key: string, fields: string[]): RecordLayoutDefinition {
    const moved = new Set(fields);
    return {
        ...definition,
        sections: definition.sections.map((section) =>
            section.key === key ? { ...section, fields } : { ...section, fields: section.fields.filter((field) => !moved.has(field)) },
        ),
    };
}
